import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { authService } from "../../lib/api/auth";
import { useAuthStore } from "../../stores/authStore";
import { Logo } from "../../components/common/Logo";

/**
 * Consumes the SSO redirect from the Shopify embedded app.
 *
 * URL contract: tokens arrive in the hash (not query) so they don't get
 * logged on the server or sent in the Referer header. Hash format:
 *   #accessToken=...&refreshToken=...&sessionId=...
 *
 * Flow: parse hash → store tokens → fetch /auth/me → set user → redirect.
 * On error: route back to "/" with an error hint in the query string.
 */
const SsoCompletePage = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const consume = async () => {
            try {
                const hash = window.location.hash.replace(/^#/, "");
                const params = new URLSearchParams(hash);
                const accessToken = params.get("accessToken");
                const refreshToken = params.get("refreshToken");

                if (!accessToken || !refreshToken) {
                    setError(t("auth.ssoMissingTokens", "Sign-in link is incomplete. Please try again from Shopify."));
                    return;
                }

                useAuthStore.getState().setTokens({
                    accessToken,
                    refreshToken,
                    expiresIn: 3600,
                });

                const me = await authService.getCurrentUser();
                if (me?.success && me.data?.user) {
                    useAuthStore.getState().setUser(me.data.user);
                    useAuthStore.setState({ isAuthenticated: true });
                }

                // Clear the hash so a refresh doesn't re-consume
                window.history.replaceState({}, "", window.location.pathname);
                navigate("/boards", { replace: true });
            } catch (err) {
                setError(t("auth.ssoFailed", "Couldn't sign you in. Please try again from Shopify."));
            }
        };

        consume();
    }, [navigate, t]);

    return (
        <div className="min-h-screen bg-background-page flex flex-col items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="flex justify-center mb-8">
                    <Logo className="w-8 h-8" />
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-800 p-8 text-center space-y-4">
                    {!error ? (
                        <>
                            <div className="mx-auto w-12 h-12 border-4 border-accent-primary border-t-transparent rounded-full animate-spin" />
                            <h2 className="text-lg font-semibold text-text-primary dark:text-neutral-100">
                                {t("auth.ssoConnecting", "Signing you in from Shopify…")}
                            </h2>
                        </>
                    ) : (
                        <>
                            <h2 className="text-lg font-semibold text-text-primary dark:text-neutral-100">
                                {t("auth.ssoFailedTitle", "Sign-in failed")}
                            </h2>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">{error}</p>
                            <button
                                type="button"
                                className="btn-primary inline-block px-6 py-2.5"
                                onClick={() => navigate("/", { replace: true })}
                            >
                                {t("auth.backToHome", "Back to home")}
                            </button>
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default SsoCompletePage;
